import apiClient from './client';

// Type definitions

export type HealthLevel = 'healthy' | 'warning' | 'critical' | 'suspended' | 'unknown';

export interface PolicyViolation {
  resource_type: string;
  resource_id: string;
  campaign_id: string;
  campaign_name: string | null;
  ad_group_id: string;
  ad_group_name: string | null;
  approval_status: string;
  review_status: string | null;
  policy_topics: string[];
  is_egregious: boolean;
}

export interface AccountHealth {
  customer_id: string;
  health_level: HealthLevel;
  customer_status: string;
  is_accessible: boolean;
  total_ads: number;
  disapproved_ads: number;
  disapproval_rate: number;
  has_egregious_violations: boolean;
  policy_topics_count: Record<string, number>;
  checked_at: string;
}

export interface AccountPolicyReport {
  customer_id: string;
  customer_status: string;
  health_level: HealthLevel;
  generated_at: string;
  total_ads: number;
  disapproved_ads: number;
  limited_ads: number;
  disapproval_rate: number;
  total_keywords: number;
  disapproved_keywords: number;
  has_egregious_violations: boolean;
  policy_topics_count: Record<string, number>;
  api_errors: string[];
  ad_violations: PolicyViolation[];
  keyword_violations: PolicyViolation[];
}

export interface PolicyWarning {
  id: number;
  google_ads_account_id: string;
  account_name: string | null;
  warning_reason: string;
  disapproval_count: number;
  disapproval_rate: number;
  policy_topics: string[];
  grace_period_days: number;
  warning_sent_at: string | null;
  reminder_count: number;
  last_reminder_sent_at: string | null;
  days_remaining: number;
  is_active: boolean;
}

export interface PolicyBlock {
  id: number;
  google_ads_account_id: string;
  account_name: string | null;
  unlink_reason: string;
  health_level_at_unlink: string | null;
  disapproval_count: number;
  disapproval_rate: number;
  policy_topics: string[];
  api_error: string | null;
  can_relink: boolean;
  unlinked_at: string | null;
  created_at: string;
}

export interface WarningsListResponse {
  warnings: PolicyWarning[];
  total: number;
}

export interface BlocksListResponse {
  blocks: PolicyBlock[];
  total: number;
}

export interface RelinkValidation {
  customer_id: string;
  can_relink: boolean;
  reason: string;
  health_level: HealthLevel;
  disapproval_rate: number;
  has_egregious_violations: boolean;
}

export interface RelinkResponse {
  success: boolean;
  message: string;
  customer_id: string;
}

export const accountMonitoringApi = {
  /**
   * Get the health status of a Google Ads account.
   * @param customerId The Google Ads customer ID
   */
  async getAccountHealth(customerId: string): Promise<AccountHealth> {
    const response = await apiClient.get<AccountHealth>(
      `/account-monitoring/health/${customerId}`
    );
    return response.data;
  },

  /**
   * Get a detailed policy report for a Google Ads account.
   * @param customerId The Google Ads customer ID
   * @param includeViolations Whether to include detailed violation list
   */
  async getPolicyReport(
    customerId: string,
    includeViolations: boolean = true
  ): Promise<AccountPolicyReport> {
    const response = await apiClient.get<AccountPolicyReport>(
      `/account-monitoring/report/${customerId}`,
      { params: { include_violations: includeViolations } }
    );
    return response.data;
  },

  /**
   * List policy warnings for the current user's accounts.
   * @param activeOnly Only show active warnings (default true)
   */
  async listWarnings(activeOnly: boolean = true): Promise<WarningsListResponse> {
    const response = await apiClient.get<WarningsListResponse>(
      '/account-monitoring/warnings',
      { params: { active_only: activeOnly } }
    );
    return response.data;
  },

  /**
   * List blocked/unlinked accounts for the current user.
   */
  async listBlocks(): Promise<BlocksListResponse> {
    const response = await apiClient.get<BlocksListResponse>(
      '/account-monitoring/blocks'
    );
    return response.data;
  },

  /**
   * Validate if a previously blocked account can be re-linked.
   * @param customerId The Google Ads customer ID
   */
  async validateRelink(customerId: string): Promise<RelinkValidation> {
    const response = await apiClient.post<RelinkValidation>(
      `/account-monitoring/validate-relink/${customerId}`
    );
    return response.data;
  },

  /**
   * Attempt to re-link a previously blocked account.
   * @param customerId The Google Ads customer ID
   */
  async relinkAccount(customerId: string): Promise<RelinkResponse> {
    const response = await apiClient.post<RelinkResponse>(
      `/account-monitoring/relink/${customerId}`
    );
    return response.data;
  },
};

export default accountMonitoringApi;
